import { DiagnosticsFooter, EmptyState, usePayload } from './primitives';
import styles from './styles.module.css';
import s from './BrandPositioningViewer.module.css';

const Ev = ({ items }) => {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <ul className={s.ev}>
      {items.map((e, i) => <li key={i} className={s.evItem}>{e}</li>)}
    </ul>
  );
};

const BrandPositioningViewer = ({ report }) => {
  const payload = usePayload(report);
  if (!report) return <EmptyState />;

  const fields = Array.isArray(payload?.fields) ? payload.fields : [];
  const byKey  = Object.fromEntries(fields.map((f) => [f.key, f]));

  const bp      = byKey['brand_positioning'];
  const vp      = byKey['value_proposition'];
  const diff    = byKey['differentiation_points'];
  const msgs    = byKey['core_messages'];
  const theme   = byKey['dominant_message_theme'];
  const promo   = byKey['promotional_message_style'];
  const tone    = byKey['brand_tone'];
  const trust   = byKey['trust_signals'];
  const quality = byKey['quality_signal_level'];

  const confidence = payload?.diagnostics?.profile_confidence_level || null;
  const missing    = Array.isArray(payload?.diagnostics?.missing_data_flags)
    ? payload.diagnostics.missing_data_flags : [];

  const diffVals  = Array.isArray(diff?.value)  ? diff.value  : [];
  const msgVals   = Array.isArray(msgs?.value)   ? msgs.value  : [];
  const trustVals = Array.isArray(trust?.value)  ? trust.value : [];

  return (
    <div className={styles.viewer}>

      {/* Summary */}
      {payload?.summary && (
        <div className={s.summaryBlock}>
          <div className={s.summaryHeader}>
            <span className={s.summaryTitle}>Summary</span>
            {confidence && (
              <span className={`${s.confBadge} ${confidence === 'High' ? s.confHigh : confidence === 'Medium' ? s.confMedium : s.confLow}`}>
                {confidence}
              </span>
            )}
          </div>
          <p className={s.summaryText}>{payload.summary}</p>
        </div>
      )}

      {/* Positioning + Style */}
      {(bp?.value || promo?.value || tone?.value) && (
        <div className={s.block}>
          <div className={s.blockHeader}>
            <span className={s.blockTitle}>Brand Identity</span>
          </div>

          {bp?.value && (
            <div className={s.heroRow}>
              <div className={s.heroLeft}>
                <span className={s.posLabel}>Positioning</span>
                <span className={s.posValue}>{bp.value}</span>
                {theme?.value && <span className={s.posTheme}>"{theme.value}"</span>}
                <Ev items={bp.evidence} />
              </div>
            </div>
          )}

          {(promo?.value || tone?.value) && (
            <div className={s.stylePair}>
              {promo?.value && (
                <div className={s.styleCell}>
                  <span className={s.styleCellLabel}>Promo style</span>
                  <span className={s.styleCellValue}>{promo.value}</span>
                  <Ev items={promo.evidence} />
                </div>
              )}
              {tone?.value && (
                <div className={s.styleCell}>
                  <span className={s.styleCellLabel}>Brand tone</span>
                  <span className={s.styleCellValue}>{tone.value}</span>
                  <Ev items={tone.evidence} />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Value proposition */}
      {vp?.value && (
        <div className={s.block}>
          <div className={s.blockHeader}>
            <span className={s.blockTitle}>Value Proposition</span>
          </div>
          <p className={s.vpText}>{vp.value}</p>
          {Array.isArray(vp.evidence) && vp.evidence.length > 0 && (
            <div className={s.vpEvidenceWrap}>
              <Ev items={vp.evidence} />
            </div>
          )}
        </div>
      )}

      {/* Differentiation + Core messages */}
      {(diffVals.length > 0 || msgVals.length > 0) && (
        <div className={s.claimsGrid}>
          {diffVals.length > 0 && (
            <div className={s.claimsCell}>
              <span className={s.claimsCellLabel}>Differentiation points</span>
              <div className={s.chips}>
                {diffVals.map((v, i) => <span key={i} className={`${s.chip} ${s.chipDiff}`}>{v}</span>)}
              </div>
              <Ev items={diff.evidence} />
            </div>
          )}
          {msgVals.length > 0 && (
            <div className={s.claimsCell}>
              <span className={s.claimsCellLabel}>Core messages</span>
              <div className={s.chips}>
                {msgVals.map((v, i) => <span key={i} className={`${s.chip} ${s.chipMsg}`}>{v}</span>)}
              </div>
              <Ev items={msgs.evidence} />
            </div>
          )}
        </div>
      )}

      {/* Trust signals + Quality level */}
      <div className={s.trustRow}>
        {trustVals.length > 0 && (
          <div className={s.trustCard}>
            <span className={s.trustCardLabel}>Trust signals</span>
            <div className={s.chips}>
              {trustVals.map((v, i) => <span key={i} className={s.chip}>{v}</span>)}
            </div>
          </div>
        )}
        {quality?.value && (
          <div className={s.qualityCard}>
            <span className={s.qualityCardLabel}>Quality signal level</span>
            <span className={s.qualityValue}>{quality.value}</span>
          </div>
        )}
      </div>

      <DiagnosticsFooter missingFlags={missing} />
    </div>
  );
};

export default BrandPositioningViewer;
